// addColumn — VQE-Edit edit-action: append (or insert at index) a SELECT
// item via SR-Edit's `add-select-item` op. The only knowledge this module
// owns is "domain-level intent" (which Column to add, which scope context to
// format against). It builds a `StructuralEdit` and delegates to `commitEdit`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove SELECT
// column.

import type { Column } from '../../../../sql-resolver/types/schema';
import { commitEdit, type CommitEditCtx } from '../commitEdit';
import { formatColumnRef, type ColumnRefs } from './selectItemHelpers';

export type AddColumnOptions = {
  /**
   * The statement's sources (the column picker's `refs`) used to qualify the
   * column reference (alias-aware, dialect-quoted). When omitted the column
   * is emitted as the bare column name.
   */
  readonly refs?: ColumnRefs | null;
  /**
   * If set, insert AFTER this 0-based SELECT-list index. Omit / `undefined`
   * to append after the last item (SR-Edit's default behavior).
   */
  readonly afterIndex?: number;
  /**
   * Override the default `formatColumnRef` output — used when the popover
   * already prepared the textual form (e.g. user typed a custom expression).
   */
  readonly textOverride?: string;
};

export async function addColumn(
  column: Column,
  ctx: CommitEditCtx,
  options: AddColumnOptions = {},
): Promise<void> {
  const text = options.textOverride ?? formatColumnRef(column, options.refs ?? null);
  await commitEdit(
    {
      op: 'add-select-item',
      text,
      ...(options.afterIndex !== undefined ? { afterIndex: options.afterIndex } : {}),
    },
    ctx,
  );
}

/**
 * Append an arbitrary expression. Used when the popover offers a non-Column
 * choice (e.g. `*`, `COUNT(*)` — future filters) or when tests want to bypass
 * the `Column → text` conversion.
 */
export async function addExpression(
  expression: string,
  ctx: CommitEditCtx,
  options: { readonly afterIndex?: number } = {},
): Promise<void> {
  await commitEdit(
    {
      op: 'add-select-item',
      text: expression,
      ...(options.afterIndex !== undefined ? { afterIndex: options.afterIndex } : {}),
    },
    ctx,
  );
}
